import { ERROR_MESSAGES, COMMENT_STATUS, DELETE_CONFIRMATION_MESSAGES, COMMENT_UI_TEXT } from '../constants/workout';
import { Comment } from '../types/workoutComments';
import { parseJwt } from './utilities';

// Helper function to get appropriate error message based on error type
export const getWorkoutCommentErrorMessage = (
  error: any,
  operation: 'delete' | 'create' | 'report' = 'delete',
  prismicData?: any
): string => {
  const errorMessage = error?.message || error?.toString() || '';
  const errorCode = error?.extensions?.code || error?.code || '';

  // Check for authorization errors (common patterns)
  const isUnauthorized =
    errorMessage.toLowerCase().includes('unauthorized') ||
    errorMessage.toLowerCase().includes('forbidden') ||
    errorMessage.toLowerCase().includes('not authorized') ||
    errorMessage.toLowerCase().includes('permission denied') ||
    errorCode === 'UNAUTHORIZED' ||
    errorCode === 'FORBIDDEN' ||
    error?.response?.status === 401 ||
    error?.response?.status === 403;

  // Check for network errors
  const isNetworkError =
    errorMessage.toLowerCase().includes('network') ||
    errorMessage.toLowerCase().includes('connection') ||
    errorCode === 'NETWORK_ERROR' ||
    !navigator.onLine;

  // Check for inappropriate content/profanity errors
  const isInappropriateContent =
    errorMessage.toLowerCase().includes('inappropriate language') ||
    errorMessage.toLowerCase().includes('inappropriate content') ||
    errorMessage.toLowerCase().includes('profanity') ||
    errorMessage.toLowerCase().includes('validation failed: comment contains inappropriate');

  if (operation === 'delete' && isUnauthorized) {
    return prismicData?.error_unauthorized_delete || ERROR_MESSAGES.COMMENT_DELETE_UNAUTHORIZED;
  }

  if (isNetworkError) {
    return prismicData?.error_network || ERROR_MESSAGES.NETWORK_ERROR;
  }

  if (isInappropriateContent) {
    return prismicData?.error_inappropriate_content || ERROR_MESSAGES.COMMENT_INAPPROPRIATE_CONTENT;
  }

  // Return operation-specific generic message (Prismic first, fallback to constants)
  switch (operation) {
    case 'delete':
      return prismicData?.error_delete_generic || ERROR_MESSAGES.COMMENT_DELETE_GENERIC;
    case 'create':
      return prismicData?.error_create_generic || ERROR_MESSAGES.COMMENT_CREATE_GENERIC;
    case 'report':
      return prismicData?.error_report_generic || ERROR_MESSAGES.COMMENT_REPORT_GENERIC;
    default:
      return prismicData?.error_delete_generic || ERROR_MESSAGES.COMMENT_DELETE_GENERIC;
  }
};

// Helper function to perform cascade delete (remove comment and all its replies)
export const performWorkoutCommentCascadeDelete = (
  comments: Comment[],
  deletedCommentId: string
): Comment[] =>
  comments
    .filter((item) => {
      // Remove the parent comment being deleted
      if (item.id === deletedCommentId) {
        return false;
      }

      // Also remove any replies to the deleted comment (in case they're stored as top-level items)
      if (item.parentId === deletedCommentId) {
        return false;
      }

      return true;
    })
    .map((item) => {
      // Clean up any replies to the deleted comment from repliedComments arrays
      if (item.repliedComments && item.repliedComments.length > 0) {
        const filteredReplies = item.repliedComments.filter((reply: Comment) => {
          // Remove replies TO the deleted comment (cascade delete)
          if (reply.parentId === deletedCommentId) {
            return false;
          }

          // Remove the reply itself if it was the item being deleted
          if (reply.id === deletedCommentId) {
            return false;
          }

          return true;
        });

        // Only create new object if replies were actually filtered
        if (filteredReplies.length !== item.repliedComments!.length) {
          return {
            ...item,
            repliedComments: filteredReplies,
            isReplied: filteredReplies.length > 0, // Update isReplied flag
          };
        }
      }

      return item;
    });

// Helper function to sort comments and replies by creation date (latest first)
const sortCommentsByLatest = (comments: Comment[]): Comment[] =>
  comments.sort((a, b) => {
    const dateA = new Date(a.createdAt || a.timestamp || 0).getTime();
    const dateB = new Date(b.createdAt || b.timestamp || 0).getTime();
    return dateB - dateA; // Latest first
  });

// Helper function to filter and deduplicate comments for new API
export const processWorkoutComments = (items: Comment[]): Comment[] => {
  // Security: Filter out ONLY deleted comments - show ACTIVE and REPORTED to prevent report abuse
  const visibleItems = items.filter(
    (item) => item.status === COMMENT_STATUS.ACTIVE || item.status === COMMENT_STATUS.REPORTED
  );

  // Deduplicate comments
  const uniqueItems = visibleItems.filter(
    (item, index, arr) => arr.findIndex((comment) => comment.id === item.id) === index
  );

  // Filter to only show top-level comments (replies should be nested)
  const topLevelComments = uniqueItems.filter((comment) => !comment.parentId);

  // Filter out deleted replies from nested repliedComments for security and sort them
  const secureComments = topLevelComments.map((comment) => ({
    ...comment,
    repliedComments: sortCommentsByLatest(
      comment.repliedComments?.filter(
        (reply: Comment) =>
          reply.status === COMMENT_STATUS.ACTIVE || reply.status === COMMENT_STATUS.REPORTED
      ) || []
    ),
  }));

  // Sort main comments by latest first
  return sortCommentsByLatest(secureComments);
};

// Helper function to deduplicate and filter paginated comments
export const processPaginatedWorkoutComments = (
  newItems: Comment[],
  existingComments: Comment[]
): Comment[] => {
  // Security: Filter out ONLY deleted comments - show ACTIVE and REPORTED to prevent report abuse
  const visibleItems = newItems.filter(
    (item) => item.status === COMMENT_STATUS.ACTIVE || item.status === COMMENT_STATUS.REPORTED
  );

  // Deduplicate comments by ID to prevent API duplicates
  const existingIds = new Set(existingComments.map((comment) => comment.id));
  const uniqueNewItems = visibleItems.filter((item) => !existingIds.has(item.id));

  // Filter to only add top-level comments (replies shouldn't be in main list)
  const topLevelNewItems = uniqueNewItems.filter((comment) => !comment.parentId);

  // Filter out deleted replies from nested repliedComments for security and sort them
  const secureComments = topLevelNewItems.map((comment) => ({
    ...comment,
    repliedComments: sortCommentsByLatest(
      comment.repliedComments?.filter(
        (reply: Comment) =>
          reply.status === COMMENT_STATUS.ACTIVE || reply.status === COMMENT_STATUS.REPORTED
      ) || []
    ),
  }));

  // Sort new comments by latest first
  return sortCommentsByLatest(secureComments);
};

// Security validation function - ensure comment is safe to display
export const isCommentSafeToDisplay = (comment: Comment | null | undefined): boolean => {
  // Never display deleted comments for security
  if (!comment || comment.status === COMMENT_STATUS.DELETED) {
    return false;
  }

  // Only display active and reported comments (prevent report abuse while maintaining security)
  return comment.status === COMMENT_STATUS.ACTIVE || comment.status === COMMENT_STATUS.REPORTED;
};

// Helper function to check if a user is a support agent
export const isSupportUser = (): boolean => {
  try {
    const jwtTokenRaw = typeof window !== 'undefined' ? localStorage.getItem('jwtToken') : null;
    if (!jwtTokenRaw) {
      return false;
    }

    const decoded = parseJwt(jwtTokenRaw);
    if (!decoded || !decoded['cognito:groups']) {
      return false;
    }

    // Check if user has the LMOD_Support_Users group
    const cognitoGroups = decoded['cognito:groups'];
    return Array.isArray(cognitoGroups) && cognitoGroups.includes('LMOD_Support_Users');
  } catch (error) {
    // Silently fail for security - don't log sensitive authentication errors
    return false;
  }
};

// Helper function to check if user can delete a comment (author, support user, or admin)
export const canUserDeleteComment = (
  comment: Comment | null | undefined,
  vimeoUserId: string | null | undefined
): boolean => {
  if (!comment || !vimeoUserId) {
    return false;
  }

  // User can delete if they are the comment author
  const isAuthor = comment.user?.id === vimeoUserId;
  
  // Support users can delete any comment
  const isSupportAgent = isSupportUser();

  return isAuthor || isSupportAgent;
};

// Helper function to determine if a comment is a reply (has parentId)
export const isCommentAReply = (comment: Comment | null | undefined): boolean => 
  !!(comment && comment.parentId);

// Helper function to get appropriate delete confirmation message
export const getDeleteConfirmationMessage = (isReply: boolean, prismicData?: any): string => {
  if (isReply) {
    return prismicData?.delete_reply_confirmation || DELETE_CONFIRMATION_MESSAGES.REPLY;
  }
  return prismicData?.delete_comment_confirmation || DELETE_CONFIRMATION_MESSAGES.COMMENT;
};

// Helper function to get multiline helper text (Prismic-ready for i18n)
export const getMultilineHelperText = (prismicData?: any): string => 
  prismicData?.multiline_helper_text || COMMENT_UI_TEXT.MULTILINE_HELPER;
